import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../assets/css/styles.css";
import dash_1 from "../assets/images/dash_1.png";
import dash_2 from "../assets/images/dash_2.png";
import dash_3 from "../assets/images/dash_3.png";
import { ArrowLeft, ArrowRight } from "../components/Icons";

const SLIDES = [
  {
    image: dash_1,
    eyebrow: "Calgary & area",
    titleLead: "Fresh from the farm,",
    titleAccent: "straight to your table",
    description:
      "Delivering health and happiness to your doorstep with quality you can trust — sourced from farmers we know by name.",
  },
  {
    image: dash_2,
    eyebrow: "Community shares",
    titleLead: "Sustainable farming,",
    titleAccent: "shared by neighbours",
    description:
      "Group buying that empowers local farmers and supports eco-friendly agriculture — better food for less, together.",
  },
  {
    image: dash_3,
    eyebrow: "Whole-animal quality",
    titleLead: "Healthy living starts",
    titleAccent: "with real food",
    description:
      "Fresh, carefully raised produce and meat to nourish your family and strengthen our community.",
  },
];

const STATS = [
  { value: "100%", label: "Locally sourced" },
  { value: "7 days", label: "Delivery after close" },
  { value: "0", label: "Middlemen markups" },
];

const AUTOPLAY_MS = 6500;

const Hero = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const goTo = useCallback((index) => {
    setActiveIndex((index + SLIDES.length) % SLIDES.length);
  }, []);

  const next = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const prev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  useEffect(() => {
    if (paused) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    timerRef.current = setTimeout(() => {
      setActiveIndex((index) => (index + 1) % SLIDES.length);
    }, AUTOPLAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [activeIndex, paused]);

  const onKeyDown = (event) => {
    if (event.key === "ArrowRight") next();
    if (event.key === "ArrowLeft") prev();
  };

  const slide = SLIDES[activeIndex];

  return (
    <header
      id="home"
      className="hero"
      aria-roledescription="carousel"
      aria-label="Farmer Boaz highlights"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKeyDown}
    >
      <div className="hero-slides">
        {SLIDES.map((item, index) => (
          <div
            key={index}
            className={`hero-slide ${index === activeIndex ? "is-active" : ""}`}
            aria-hidden={index !== activeIndex}
          >
            <img
              src={item.image}
              alt=""
              loading={index === 0 ? "eager" : "lazy"}
            />
          </div>
        ))}
      </div>
      <div className="hero-scrim" />

      <div className="container">
        <div className="hero-content">
          <div className="hero-copy-fade" key={activeIndex}>
            <span className="eyebrow hero-eyebrow">{slide.eyebrow}</span>
            <h1 className="hero-title">
              {slide.titleLead} <em>{slide.titleAccent}</em>
            </h1>
            <p className="hero-text">{slide.description}</p>
            <div className="hero-actions">
              <Link to="/order-now" className="btn btn--lg">
                Order Now
                <ArrowRight />
              </Link>
              <a href="/#about" className="btn btn--lg btn--outline">
                How it works
              </a>
            </div>
          </div>

          <div className="hero-stats">
            {STATS.map((stat) => (
              <div className="hero-stat" key={stat.label}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hero-controls">
        <div className="hero-dots" role="tablist" aria-label="Choose slide">
          {SLIDES.map((item, index) => (
            <button
              key={index}
              type="button"
              role="tab"
              className={`hero-dot ${index === activeIndex ? "is-active" : ""}`}
              aria-selected={index === activeIndex}
              aria-label={`Slide ${index + 1} of ${SLIDES.length}`}
              onClick={() => goTo(index)}
            />
          ))}
        </div>
        <button type="button" className="hero-arrow" onClick={prev} aria-label="Previous slide">
          <ArrowLeft />
        </button>
        <button type="button" className="hero-arrow" onClick={next} aria-label="Next slide">
          <ArrowRight />
        </button>
      </div>
    </header>
  );
};

export default Hero;
